import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { ClockIcon } from '@heroicons/react/24/outline';

import MeditationSettings from './MeditationSettings';
import MeditationPlayer from './MeditationPlayer';
import MeditationSummary from './MeditationSummary';
import { PHASES, SOUND_OPTIONS, PREMIUM_ROLE_ID, INTRO_AUDIO } from './constants';
import UrlConfig from '../../../../data/UrlConfig';

const INTRO_PHASE_OBJECT = {
    name: 'Preparación',
    icon: ClockIcon,
    description: 'La meditación comenzará en breve. Concéntrate en tu respiración.'
};

const MEDITATION_SETTINGS_KEY = 'manifiesta_meditation_settings';

const MeditationSevenPhases = ({ onProceed, nextActivity, forcePresenceMode }) => {
    const { user } = useAuth();
    const isPremium = useMemo(() => user?.roles?.includes(PREMIUM_ROLE_ID), [user]);
    
    const [view, setView] = useState('settings'); 
    const [currentPhaseIndex, setCurrentPhaseIndex] = useState(null); 
    const [timeLeftInPhase, setTimeLeftInPhase] = useState(0);
    const [totalTimeMeditated, setTotalTimeMeditated] = useState(0);
    
    const [phaseDurations, setPhaseDurations] = useState(PHASES.map(() => 5));
    const [activePhases, setActivePhases] = useState(PHASES.map(() => true));
    const [activeGuidedMeditations, setActiveGuidedMeditations] = useState(PHASES.map(() => false));

    const [settings, setSettings] = useState({
        background: 'none',
        gong: 'gong1',
        enableGong: true,
        enableVoice: true,
        enableIntro: true,
    });

    const [backgroundVolume, setBackgroundVolume] = useState(0.3);

    const [revelations, setRevelations] = useState([]);
    const [refreshRevelations, setRefreshRevelations] = useState(0);

    const audioRefs = {
        startEnd: useRef(null),
        gong: useRef(null),
        background: useRef(null),
        voice: useRef(null),
        guided: useRef(null),
        intro: useRef(null),
    };
    const timerRefs = useRef({});

    // (El resto de hooks y funciones no necesitan cambios)
    useEffect(() => {
        try {
            const savedSettings = localStorage.getItem(MEDITATION_SETTINGS_KEY);
            if (savedSettings) {
                const parsedSettings = JSON.parse(savedSettings);
                if (parsedSettings.phaseDurations) setPhaseDurations(parsedSettings.phaseDurations);
                if (parsedSettings.activePhases) setActivePhases(parsedSettings.activePhases);
                if (parsedSettings.activeGuidedMeditations) setActiveGuidedMeditations(parsedSettings.activeGuidedMeditations);
                if (parsedSettings.settings) setSettings(parsedSettings.settings);
                if (typeof parsedSettings.backgroundVolume === 'number') {
                    setBackgroundVolume(parsedSettings.backgroundVolume);
                }
            }
        } catch (error) {
            console.error("Error al cargar la configuración de meditación desde localStorage:", error);
        }
    }, []);
    
    useEffect(() => {
        const fetchRevelations = async () => {
            if (!user) {
                setRevelations([]);
                return;
            }
            try {
                const response = await fetch(UrlConfig.meditation_getRevelations, { credentials: 'include' });
                if (response.ok) {
                    const result = await response.json();
                    setRevelations(result.data || []);
                }
            } catch (error) {
                console.error("Error al obtener revelaciones:", error);
            }
        };

        fetchRevelations();
    }, [user, refreshRevelations]);


    useEffect(() => {
        try {
            const settingsToSave = {
                phaseDurations,
                activePhases,
                activeGuidedMeditations,
                settings,
                backgroundVolume,
            };
            localStorage.setItem(MEDITATION_SETTINGS_KEY, JSON.stringify(settingsToSave));
        } catch (error) {
            console.error("Error al guardar la configuración de meditación en localStorage:", error);
        }
    }, [phaseDurations, activePhases, activeGuidedMeditations, settings, backgroundVolume]);

    useEffect(() => {
        if (audioRefs.background.current) {
            audioRefs.background.current.volume = backgroundVolume;
        }
    }, [backgroundVolume]);

    const playAudio = (audioRef) => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    if (error.name !== 'AbortError') {
                        console.error("Error al reproducir el audio:", error);
                    }
                });
            }
        }
    };
    
    const clearAllTimers = () => {
        Object.values(timerRefs.current).forEach(clearTimeout);
    };

    const findNextActivePhase = (startIndex) => {
        for (let i = startIndex + 1; i < PHASES.length; i++) {
            if (activePhases[i]) return i;
        }
        return -1;
    };

    const stopAllAudio = () => {
        Object.values(audioRefs).forEach(ref => {
            if (ref.current && !ref.current.paused) {
                ref.current.pause();
                ref.current.currentTime = 0;
            }
        });
        clearAllTimers();
    };

    const playPhaseAudioSequence = () => {
        clearAllTimers();
        if (currentPhaseIndex === null) return; 

        const phase = PHASES[currentPhaseIndex];
        const isGuidedActive = activeGuidedMeditations[currentPhaseIndex];

        const playGuided = (delay) => {
            if (isGuidedActive && phase.guidedAudio && audioRefs.guided.current) {
                timerRefs.current.guidedDelay = setTimeout(() => {
                    audioRefs.guided.current.src = phase.guidedAudio;
                    playAudio(audioRefs.guided);
                }, delay);
            }
        };

        if (settings.enableVoice && phase.voicePrompt && audioRefs.voice.current) {
            timerRefs.current.voiceDelay = setTimeout(() => {
                const voiceAudio = audioRefs.voice.current;
                voiceAudio.src = phase.voicePrompt;
                voiceAudio.onended = () => playGuided(5000); 
                playAudio(audioRefs.voice);
            }, 3000);
        } else {
            playGuided(7000);
        }
    };
    
    useEffect(() => {
        if (view !== 'playing') return;
    
        if (timeLeftInPhase <= 0 && currentPhaseIndex !== null) {
            const nextPhaseIndex = findNextActivePhase(currentPhaseIndex);
            
            if (nextPhaseIndex !== -1) {
                if (settings.enableGong) {
                    playAudio(audioRefs.gong);
                }
                setCurrentPhaseIndex(nextPhaseIndex);
            } else {
                if (audioRefs.background.current) audioRefs.background.current.pause();
                if (audioRefs.guided.current) audioRefs.guided.current.pause();
                clearAllTimers();
    
                const endAudio = audioRefs.startEnd.current;
    
                if (endAudio) {
                    endAudio.onended = () => {
                        setView('summary');
                        endAudio.onended = null;
                        endAudio.onerror = null;
                    };
    
                    endAudio.onerror = () => {
                        console.error("No se pudo reproducir el audio de finalización.");
                        setView('summary');
                        endAudio.onended = null;
                        endAudio.onerror = null;
                    };
    
                    playAudio(audioRefs.startEnd);
    
                } else {
                    setView('summary');
                }
            }
            return;
        }
    
        if (currentPhaseIndex === null || timeLeftInPhase <= 0) return;
        
        const interval = setInterval(() => {
            setTimeLeftInPhase(prev => Math.max(0, prev - 1));
            setTotalTimeMeditated(prev => prev + 1);
        }, 1000);
    
        return () => clearInterval(interval);
    }, [view, timeLeftInPhase]);
    
    useEffect(() => {
        if (view === 'playing' && currentPhaseIndex !== null) {
            const newDurationInSeconds = phaseDurations[currentPhaseIndex] * 60;
            setTimeLeftInPhase(newDurationInSeconds);
            playPhaseAudioSequence();
        }
    }, [currentPhaseIndex, view]);

    const handlePhaseToggle = (index) => setActivePhases(c => c.map((p, i) => i === index ? !p : p));
    const handleGuidedToggle = (index) => setActiveGuidedMeditations(c => c.map((g, i) => i === index ? !g : g));
    const handleSettingsChange = (key, value) => setSettings(p => ({ ...p, [key]: value }));
    const handleDurationChange = (index, value) => setPhaseDurations(p => { const n = [...p]; n[index] = value; return n; });
    const handleVolumeChange = (newVolume) => {
        setBackgroundVolume(parseFloat(newVolume));
    };

    const handleStartMeditation = () => {
        const firstActiveIndex = findNextActivePhase(-1);
        if (firstActiveIndex === -1) {
            alert("Por favor, activa al menos una fase para comenzar.");
            return;
        }

        stopAllAudio(); 
        
        const gongSound = SOUND_OPTIONS.gongs.find(g => g.id === settings.gong);
        if (gongSound?.url && audioRefs.gong.current) {
            audioRefs.gong.current.src = gongSound.url;
        }

        setView('playing');
        setCurrentPhaseIndex(null);
        setTotalTimeMeditated(0);
        setTimeLeftInPhase(0);

        const bgSound = SOUND_OPTIONS.background.find(s => s.id === settings.background);
        if (bgSound?.url) {
            audioRefs.background.current.src = bgSound.url;
            audioRefs.background.current.loop = true;
            audioRefs.background.current.volume = backgroundVolume;
            playAudio(audioRefs.background);
        }
        playAudio(audioRefs.startEnd);

        const startFirstPhase = () => {
            const introAudio = audioRefs.intro.current;
            if (introAudio) {
                introAudio.onended = null;
                introAudio.onerror = null;
            }

            if (settings.enableGong) {
                playAudio(audioRefs.gong);
            }
            
            setCurrentPhaseIndex(firstActiveIndex); 
        };

        if (settings.enableIntro && INTRO_AUDIO.url) {
            timerRefs.current.introDelay = setTimeout(() => {
                const introAudio = audioRefs.intro.current;
                introAudio.src = INTRO_AUDIO.url;
                introAudio.onended = () => { timerRefs.current.postIntroDelay = setTimeout(startFirstPhase, 7000); };
                introAudio.onerror = () => { 
                    console.error("Error al cargar audio de intro."); 
                    timerRefs.current.postIntroDelay = setTimeout(startFirstPhase, 7000); 
                };
                playAudio(audioRefs.intro);
            }, 7000);
        } else {
            timerRefs.current.postGongDelay = setTimeout(startFirstPhase, 7000);
        }
    };

    const handleStop = () => {
        stopAllAudio();
        setView('summary');
    };
    
    const handleRestart = () => {
        stopAllAudio();
        setView('settings');
        setCurrentPhaseIndex(null);
        setTotalTimeMeditated(0);
        setTimeLeftInPhase(0);
    };
    
    const handleRevelationSaved = () => {
        setRefreshRevelations(prev => prev + 1);
    };

    const totalDuration = useMemo(() => {
        return phaseDurations.reduce((acc, duration, index) => {
            if (activePhases[index]) return acc + (duration * 60);
            return acc;
        }, 0);
    }, [phaseDurations, activePhases]);

    return (
        <div className="bg-gray-950 text-white min-h-screen w-full flex justify-center py-8 px-4">
            <div className="w-full max-w-3xl">
                <audio ref={audioRefs.startEnd} src={SOUND_OPTIONS.startEnd.default.url} />
                <audio ref={audioRefs.gong} />
                <audio ref={audioRefs.background} />
                <audio ref={audioRefs.voice} />
                <audio ref={audioRefs.guided} />
                <audio ref={audioRefs.intro} />
            
                {view === 'settings' && (
                    <MeditationSettings
                        settings={settings}
                        phaseDurations={phaseDurations}
                        activePhases={activePhases}
                        activeGuidedMeditations={activeGuidedMeditations}
                        isPremium={isPremium}
                        backgroundVolume={backgroundVolume}
                        revelations={revelations}
                        onSettingsChange={handleSettingsChange}
                        onDurationChange={handleDurationChange}
                        onPhaseToggle={handlePhaseToggle}
                        onGuidedToggle={handleGuidedToggle}
                        onVolumeChange={handleVolumeChange}
                        onStart={handleStartMeditation}
                        // --- CAMBIO CLAVE: Pasar las props a este componente ---
                        onProceed={onProceed}
                        nextActivity={nextActivity}
                        forcePresenceMode={forcePresenceMode} // Forzamos el modo Presencia para esta meditación
                    />
                )}
                {view === 'playing' && (
                    <MeditationPlayer
                        displayedPhase={currentPhaseIndex === null ? INTRO_PHASE_OBJECT : PHASES[currentPhaseIndex]}
                        timeLeft={timeLeftInPhase}
                        onStop={handleStop}
                        totalDuration={totalDuration}
                        timeElapsed={totalTimeMeditated}
                        isIntro={currentPhaseIndex === null}
                        backgroundVolume={backgroundVolume}
                        onVolumeChange={handleVolumeChange}
                    />
                )}
                {view === 'summary' && (
                    <MeditationSummary 
                        onRestart={handleRestart} 
                        totalTime={totalTimeMeditated} 
                        onRevelationSaved={handleRevelationSaved}
                    />
                )}
            </div>
        </div>
    );
};

export default MeditationSevenPhases;